/* eslint-disable no-use-before-define,require-jsdoc */
/* eslint-disable implicit-arrow-linebreak,no-promise-executor-return,arrow-body-style,
no-nested-ternary */

export const def = (x: any): boolean => typeof x !== 'undefined'
export const isFn = (x: unknown): x is Function => typeof x === 'function'
export const isNum = (x: unknown): x is number => typeof x === 'number'
export const isBool = (x: unknown): x is boolean => typeof x === 'boolean'
export const isStr = (x: unknown): x is string => def(x) && (x as any)?.constructor === String
export const isObj = (x: unknown): x is object => def(x) && (x as any)?.constructor === Object
export const isArr = (x: unknown): x is any[] => def(x) && (x as any)?.constructor === Array
export const isFloat = (x: unknown): x is number => def(x) && isNum(x) && !!(x % 1)

/**
 * If `value` is an array, returns its first item, otherwise returns `value`.
 *
 * @param value
 */
export const getFirstItem = <T>(value: T | T[]): T => (
  value && isArr(value)
    ? value[0]
    : value as T
)

type PartialTuple<
  TUPLE extends any[],
  EXTRACTED extends any[] = [],
> =
// If the tuple provided has at least one required value
  TUPLE extends [infer NEXT, ...infer REMAINING]
      // recurse back in to this type with one less item
      // in the original tuple, and the latest extracted value
      // added to the extracted list as optional
    ? PartialTuple<REMAINING, [...EXTRACTED, NEXT?]>
      // else if there are no more values,
      // return an empty tuple so that too is a valid option2
    : [...EXTRACTED, ...TUPLE]

type PartialParameters<FN extends (...args: any[]) => any> =
  PartialTuple<Parameters<FN>>

type RemainingParameters<
  PROVIDED extends any[],
  EXPECTED extends any[],
> =
  // if the expected array has any required items…
  EXPECTED extends [infer _E, ...infer REMAINING_EXPECTED]
    // if the provided array has at least one required item
    ? PROVIDED extends [infer _P, ...infer REMAINING_PROVIDED]
      // if the type is correct, recurse with one item less in each array type
      ? RemainingParameters<REMAINING_PROVIDED, REMAINING_EXPECTED>
      // else the remaining args is unchanged
      : EXPECTED
    // else there are no more arguments
    : []

type CurriedFunctionOrReturnValue<
  PROVIDED extends any[],
  FN extends (...args: any[]) => any,
> =
  RemainingParameters<PROVIDED, Parameters<FN>> extends [any, ...any[]]
    ? CurriedFunction<PROVIDED, FN>
    : ReturnType<FN>

export type CurriedFunction<
  PROVIDED extends any[],
  FN extends (...args: any[]) => any,
> =
  <PASSED extends PartialTuple<RemainingParameters<PROVIDED, Parameters<FN>>>>(
    ...args: PASSED
  ) => CurriedFunctionOrReturnValue<[...PROVIDED, ...PASSED], FN>

/**
 * Currying by using Variadic Functions.
 *
 * Empty Accumulator Invocation is being disabled by Error
 *
 * ie:
 *
 * const sum3 = curry((x, y, z) => x + y + z)
 * sum3(1,2,3) // 6
 * sum3(1)(2)(3) // 6
 * //sum3()()()(1,2,3) // 6 - empty accumulator invocation is disabled
 * sum3(1)(2,3) // 6
 * //sum3()()()(1)()()(2,3) // 6 - empty accumulator invocation is disabled
 *
 * src: https://hackernoon.com/currying-in-js-d9ddc64f162e
 *
 * @param targetFn      the function to curry
 * @param existingArgs  args to pre-apply (usually none at the call site)
 */
export function curry<
  FN extends(...args: any[]) => any,
  STARTING_ARGS extends PartialParameters<FN>,
>(targetFn: FN, ...existingArgs: STARTING_ARGS): CurriedFunction<STARTING_ARGS, FN> {
  // console.debug('curry fn:', targetFn)

  return (...xs: any[]) => {
    // console.debug('curry xs:', ...args)

    if (xs.length === 0) {
      throw Error('EMPTY INVOCATION')
    }

    const totalArgs = [...existingArgs, ...xs]

    if (totalArgs.length >= targetFn.length) {
      return targetFn(...totalArgs)
    }

    return curry(targetFn, ...totalArgs as PartialParameters<FN>)
  }
}

/**
 * Functional Composition.
 *
 * Function composition is a mathematical concept that allows you to combine
 * two or more functions into a new function.
 *
 * important: if any of the composed functions use currying,
 * make sure they are at the TOP of the combine()
 * otherwise, the trailing functions will receive a function as parameter,
 * instead of the expected type.
 *
 * RULE OF THE THUMB: ORDER MATTERS
 *
 * A key to function composition is having functions that are composable.
 * A composable function should have 1 input argument and 1 output value.
 * That output value can be used as an input argument of the next composable function.
 * You can turn any function into a composable function by currying the function.
 *
 * src: https://hackernoon.com/javascript-functional-composition-for-every-day-use-22421ef65a10
 *
 * @param functions
 */
export const compose = (...functions: Function[]) => (data: any) => functions.reduceRight(
  (value, func) => func(value),
  data,
)

/**
 * Functional Composition in Reverse Order.
 *
 * important: if any of the composed functions use currying,
 * make sure they are at the BOTTOM of the pipe()
 * otherwise, the trailing functions will receive a function as parameter,
 * instead of the expected type.
 *
 * RULE OF THE THUMB: ORDER MATTERS
 * src: https://hackernoon.com/javascript-functional-composition-for-every-day-use-22421ef65a10
 *
 * @param functions
 */
export const pipe = (...functions: Function[]) => (data: any) => functions.reduce(
  (value, func) => func(value),
  data,
)

/**
 * Wraps piped functions with Promise.
 *
 * @param functions
 */
export const asyncpipe = (...functions: Function[]) => (param: any) => functions.reduce(
  async (result, next) => next(await result),
  param,
)

/**
 * Async Composition in Reverse Order with Spread Parameters
 *
 * Used to pipe async functions with N parameters
 *
 * @param fns
 */
export const asyncPipeSpread = (...fns: Function[]) => async (...args: any[]) => fns.reduce<any>(
  async (result, fn) => fn(...(await result).flat()),
  args,
)

/**
 * Executes {@link asyncPipeSpread} for the functions passed as fns only if the result of executing
 * `condition` is truthy. Otherwise, returns `args`.
 *
 * @param condition a function whose result decides whether fns are executed
 */
export const asyncPipeSpreadIf = (
  condition: Function,
) => (...fns: Function[]) => async (...args: any[]) => (
  condition(...args)
    ? await asyncPipeSpread(...fns)(...args)
    : args)

/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 *
 * src: https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
 *
 * @param objects - Objects to merge
 */
export const mergeDeep = (...objects: any[]): any => {
  const isObject = (obj: any) => obj && typeof obj === 'object'
  return objects.reduce((prev, obj) => {
    const mergeResult = prev
    Object.keys(obj).forEach((key) => {
      const pVal = prev[key]
      const oVal = obj[key]
      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        mergeResult[key] = [...pVal, ...oVal]
          .filter((element, index, array) => array.indexOf(element) === index)
      } else if (isObject(pVal) && isObject(oVal)) {
        mergeResult[key] = mergeDeep(pVal, oVal)
      } else {
        mergeResult[key] = oVal
      }
    })
    return mergeResult
  }, {})
}

/**
 * Sets symbol in an object, given the key is not a Namespace
 *
 * symbol can be either string or Symbol
 *
 * @param value  the value to set
 * @param symbol the key of the value
 * @param obj    the object to set the value in
 */
export const setKey = curry((value: any, symbol: string | symbol, obj: Object) => {
  // eslint-disable-next-line no-param-reassign
  obj[symbol] = value
  return obj
})

/**
 * Reads symbol key content from an object
 *
 * @param symbol the key of the value
 * @param obj    the object to set the value in
 */
export const getKey = curry((symbol: any, obj: any) => obj && obj[symbol])

/**
 * Deletes symbol in object if symbol exists
 *
 * @param symbol the key of the value
 * @param obj    the object to set the value in
 */
export const deleteKey = curry((symbol: any, obj: any) => {
  if (obj[symbol]) {
    // eslint-disable-next-line no-param-reassign
    delete obj[symbol]
  }
  return obj
})

/**
 * Calls `fn` `count` times and returns all the results in an array.
 *
 * @param count the number of times to call `fn`
 * @param fn    the function to call `count` times
 */
export const repeat = <T>(count: number, fn: (i: number) => T): T[] => {
  // Array.from({ length: count }, (e, i) => fn(i))
  const result: T[] = []
  for (let x = 0; x < count; x++) {
    result.push(fn(x))
  }
  return result
}

/**
 * Executes `fn` for each element in `elements` and returns an array with the results.
 *
 * @param fn
 */
export const bulk = (
  fn: Function,
) => (...elements: any[]) => elements.map(element => element && fn(element))

/**
 * Simple debounce function.
 *
 * @param func      the function to debounce
 * @param timeout   the debouncing period
 * @param leading   whether or not use leading debounce
 */
export const debounce = (func: Function, timeout = 300, leading = false) => {
  let timer: any
  return (...args: any[]) => {
    leading && !timer && func(...args)

    clearTimeout(timer)
    timer = setTimeout(() => {
      leading
        ? (timer = undefined)
        : func(...args)
    }, timeout)

    return timer
  }
}

/**
 * Simple throttling function.
 *
 * @param func     the function to throttle
 * @param timeout  the throttling period
 */
export const throttle = (func: Function, timeout = 300) => {
  let lastCalled = 0
  return (...args: any[]) => {
    const now = new Date().getTime()
    if (now - lastCalled < timeout) {
      return null
    }
    lastCalled = now
    return func(...args)
  }
}

/**
 * Chains promises one after another, effectively serializing concurrent calls to `fn`.
 *
 * @param fn  synchronization target
 */
export const synchronized = (fn: Function) => {
    let tail = Promise.resolve()

    return (...args: any): Promise<any> => {
      const result = tail.then(() => fn(...args))
      tail = result.catch(() => {})
      return result
    }
  }

/**
 * Simple waiting function
 *
 * @param ms time in ms to wait before returning
 */
export const wait = (
  ms: number,
): Promise<void> => new Promise((resolve) => { setTimeout(resolve, ms) })

/**
 * Simple polling function
 *
 * @param func      function to call in each iteration
 * @param predicate predicate to check in each iteration
 * @param timeout   time in ms to wait between each iteration
 * @param limit     maximum number of tries before returning
 */
export const poll = async (func: Function, predicate: Function, timeout = 1000, limit = 10) => {
  try {
    let requestLimit = limit
    let result = await func()

    while (!predicate(result) && requestLimit > 1) {
      requestLimit--
      await wait(timeout)
      result = await func()
    }

    return result
  } catch (e) {
    console.error(e)

    return null
  }
}

/**
 * Helper function to replace {@link React.Children.map}.
 *
 * @param children the children to reduce
 */
export const arrayOf = <T>(children: T|T[]): T[] => (
  children || children === 0
    ? (isArr(children) && children) || [children]
    : []
)

/**
 * Joins defined `items` in a single string, separated by whitespaces.
 *
 * @param items
 */
export const classNames = (...items: (string | boolean)[]): string => items.filter(item => item).join(' ')

/**
 * Gets `x` from the `theme`'s palette.
 *
 * @param theme     the theme
 * @param x         the color to get
 * @param important whether the color style should be marked with !important
 */
export const getThemeColor = (theme: any, x: string, important?: boolean): string | undefined => {
  if (!x || x.includes('#')) return x

  const [color, variant] = x.includes('.')
    ? x.split('.') // text.primary
    : x.split(/(?=[A-Z])/).map(t => t.toLowerCase()) // textPrimary

  // If we inline this, it'll return `'undefined'` (as a string) if the color
  // is not part of the theme
  const themeColor = theme.palette[color]?.[variant]
    || theme.palette?.[color]?.[500]
    || theme.palette?.[color]?.main

  return themeColor
    ? `${themeColor}${PLATFORM === 'web' && important ? '!important' : ''}`
    : undefined
}

/**
 * Maps module names to translation file names, so that we can group the translations of some
 * modules together.
 *
 * a map object with entries module name => translation file name
 */
export const translationsMap: Record<string, string> = {
  request: 'ticket',
  support: 'guest',
  registration: 'guest',
  serviceItem: 'device',
  dashboard: 'common',
  error: 'common',
  textTemplate: 'ticket',
  feedback: 'user',
  camera: 'common',
  typeExtension: 'common',
  adminNotice: 'common',
  xEcoApm: 'apmGo',
  xEcoCamera: 'apmGo',
}

export { default as PlatformEvent } from './event'

/**
 * Temporary definition so JS files can still use `Gaia.PlatformEvent`
 *
 * @deprecated Use `import { type PlatformEvent } from 'lib/util'` if possible
 *
 * @typedef {import('./event').PlatformEvent} Gaia.PlatformEvent
 */
